import { useEffect } from 'react';

const GCLID_STORAGE_KEY = 'gclid';

/**
 * Extract GCLID from _gcl_aw cookie (Google Ads Click Identifier)
 * Cookie format: _gcl_aw=GCL.timestamp.gclid_value
 * @returns The GCLID string or undefined if not found
 */
const getGclidFromCookie = (): string | undefined => {
  try {
    if (typeof window === 'undefined') return undefined;

    const gclAwCookie = document.cookie
      .split('; ')
      .find((row) => row.startsWith('_gcl_aw='));

    if (gclAwCookie) {
      // Format: _gcl_aw=GCL.timestamp.gclid_value
      const cookieValue = gclAwCookie.split('=')[1];
      const parts = cookieValue.split('.');

      // The gclid is the third part (index 2)
      if (parts.length >= 3 && parts[0] === 'GCL') {
        return parts[2];
      }
    }
  } catch (error) {
    console.error('Error getting gclid from _gcl_aw cookie:', error);
  }
  return undefined;
};

/**
 * Get the stored GCLID from sessionStorage
 * @returns The GCLID string or undefined if not found
 */
export const getGclid = (): string | undefined => {
  try {
    if (typeof window === 'undefined') return undefined;
    const stored = sessionStorage.getItem(GCLID_STORAGE_KEY);
    return stored || undefined;
  } catch (error) {
    console.error('Error getting gclid:', error);
    return undefined;
  }
};

/**
 * Store GCLID in sessionStorage (only if it doesn't already exist)
 * @param value - The GCLID value to store
 * @returns true if stored, false if already exists or error
 */
const setGclid = (value: string): boolean => {
  try {
    if (typeof window === 'undefined') return false;

    // Only store if it doesn't already exist (first-touch attribution)
    const existing = sessionStorage.getItem(GCLID_STORAGE_KEY);
    if (existing) {
      return false; // Already exists, don't overwrite
    }

    sessionStorage.setItem(GCLID_STORAGE_KEY, value);
    return true;
  } catch (error) {
    console.error('Error setting gclid:', error);
    return false;
  }
};

/**
 * Hook to capture and store GCLID from URL or cookie on first page load
 * This should be called once in _app.tsx
 *
 * Captures the gclid parameter in this priority order:
 * 1. From sessionStorage (if already captured in current session)
 * 2. From URL query parameter (?gclid=xxx)
 * 3. From _gcl_aw cookie (Google Ads fallback)
 *
 * Once captured, it's stored in sessionStorage for the entire session duration.
 * This ensures consistent attribution even if the user navigates to
 * pages without the gclid parameter.
 */
export const useGclidTracking = (): void => {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      // Check if gclid already exists in sessionStorage
      const existing = getGclid();
      if (existing) {
        // Already captured, don't override
        return;
      }

      // Priority 1: Try to capture from URL
      const urlParams = new URLSearchParams(window.location.search);
      const gclidFromUrl = urlParams.get('gclid');

      if (gclidFromUrl) {
        const stored = setGclid(gclidFromUrl);
        if (stored) {
          console.log('GCLID captured from URL:', gclidFromUrl);
        }
        return;
      }

      // Priority 2: Fallback to _gcl_aw cookie
      const gclidFromCookie = getGclidFromCookie();
      if (gclidFromCookie) {
        const stored = setGclid(gclidFromCookie);
        if (stored) {
          console.log('GCLID captured from _gcl_aw cookie:', gclidFromCookie);
        }
      }
    } catch (error) {
      console.error('Error in useGclidTracking:', error);
    }
  }, []); // Run only once on mount
};
